import { COUNTDOWN_STARTED } from '../constants/challenges.constants';

/**
 * Pure formatter: returns a human-readable countdown string for a target date.
 * Tiered display: "2d 5h" / "3h 12m" / "45m" / "Started" (no seconds).
 */
export const getCountdownDisplay = (targetDate: Date | null): string => {
  if (!targetDate || isNaN(targetDate.getTime())) return '';

  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs <= 0) return COUNTDOWN_STARTED;

  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays}d ${diffHours % 24}h`;
  }

  if (diffHours > 0) {
    return `${diffHours}h ${diffMins % 60}m`;
  }

  return `${diffMins}m`;
};

export interface CountdownSegments {
  days: string;
  hours: string;
  minutes: string;
}

/**
 * Returns zero-padded days, hours, minutes for segmented digital display.
 * Always two digits each (00–99). Returns null when target is in the past or invalid.
 */
export const getCountdownSegments = (
  targetDate: Date | null
): CountdownSegments | null => {
  if (!targetDate || isNaN(targetDate.getTime())) return null;

  const now = new Date();
  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs <= 0) return null;

  const totalMins = Math.floor(diffMs / (1000 * 60));
  const days = Math.floor(totalMins / (60 * 24));
  const hours = Math.floor((totalMins % (60 * 24)) / 60);
  const minutes = totalMins % 60;

  return {
    days: String(days).padStart(2, '0'),
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
  };
};
